import React from 'react';
import styled from 'styled-components';
import { classic } from '~/themes';

type IProps = {
  color: 'primary' | 'secondary';
  textColor?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  disabled?: boolean;
  hidden?: boolean;
  type?: 'button' | 'submit' | 'reset';
  opaque?: boolean;
  width?: string;
  minWidth?: string;
  minHeight?: string;
  ref?: any;
  margin?: string;
  secondaryText?: string;
  withoutTopBorderRadius?: boolean;
};

export default function Button({
  color,
  textColor,
  onClick,
  children,
  disabled,
  minHeight,
  hidden,
  type,
  opaque,
  width,
  minWidth,
  margin,
  ref,
  withoutTopBorderRadius,
}: IProps) {
  return (
    <Container
      color={color}
      disabled={disabled}
      hidden={hidden}
      $minWidth={minWidth}
      onClick={onClick}
      opaque={opaque}
      ref={ref}
      $textColor={textColor}
      type={type}
      width={width}
      $margin={margin}
      $minHeight={minHeight}
      $withoutTopBorderRadius={withoutTopBorderRadius}
    >
      {children}
    </Container>
  );
}

const Container = styled.button<{
  color: 'primary' | 'secondary';
  disabled?: boolean;
  hidden?: boolean;
  $textColor?: string | 'white';
  opaque?: boolean;
  width?: string;
  $minWidth?: string;
  $minHeight?: string;
  withoutIconMargin?: boolean;
  textTransform?: string;
  $margin?: string;
  $withoutTopBorderRadius?: boolean;
}>`
  align-items: center;
  border-radius: ${({ $withoutTopBorderRadius }) =>
    $withoutTopBorderRadius ? '0 0 16px 16px' : '16px'};
  border: none;
  box-shadow: 0px -4px 3px 0px rgba(0, 0, 0, 0.25) inset;
  background: ${({ color }) => {
    if (color === 'primary')
      return `linear-gradient(180deg, #53D2D7 0%, #27C4CA 100%)`;
    if (color === 'secondary')
      return `linear-gradient(180deg, #EA7A49 0%, #EF6226 100%)`;

    return 'transparent';
  }};
  color: ${({ $textColor }) =>
    $textColor ? $textColor : `${classic.token?.colorWhite}`};

  cursor: ${({ disabled, hidden }) =>
    disabled || hidden ? 'auto' : 'pointer'};
  display: ${({ hidden }) => (hidden ? 'hidden' : 'flex')};
  flex-direction: row;
  min-height: ${({ $minHeight }) => $minHeight ?? '42px'};
  min-width: ${({ $minWidth }) => $minWidth ?? '200px'};
  font-family: 'Riffic Free', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  justify-content: center;
  letter-spacing: normal;
  margin: ${({ $margin }) => ($margin ? $margin : '0')};
  opacity: ${({ disabled, hidden }) => {
    if (hidden) return 0;
    if (disabled) return 0.5;

    return 1;
  }};
  padding: 8px 24px;
  pointer-events: ${({ disabled, hidden }) =>
    disabled || hidden ? 'none' : 'auto'};
  text-transform: ${({ textTransform }) => textTransform ?? 'uppercase'};
  transition: all 0.6s linear;
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
  width: ${({ width }) => width ?? '200px'};
  outline: none;
  @media screen and (max-height: 800px) {
    font-size: 1rem;
    padding: 8px 14px;
    min-width: ${({ $minWidth }) => $minWidth ?? '100px'};
    width: ${({ width }) => width ?? 'auto'};
  }

  @media screen and (max-width: 600px) {
    font-size: 1rem;
    padding: 8px 14px;
    min-width: ${({ $minWidth }) => $minWidth ?? '100px'};
    width: ${({ width }) => width ?? 'auto'};
  }

  @media screen and (max-width: 450px) {
    font-size: 1rem;
    padding: 8px 14px;
    min-width: ${({ $minWidth }) => $minWidth ?? '100px'};
    width: ${({ width }) => width ?? 'auto'};
  }
  @media screen and (max-height: 450px) {
    margin: ${({ $margin }) => ($margin ? $margin : '5px')};
    padding: 5px 10px;
    min-height: ${({ $minHeight }) => $minHeight ?? '32px'};
  }

  &:focus {
    background: ${({ color }) => {
      if (color === 'primary') return `${classic.token?.colorPrimaryBgHover}`;
      if (color === 'secondary')
        return `${classic.token?.colorBorderSecondary}`;

      return 'transparent';
    }};
    font-weight: bold;
    outline: none;
  }

  &:hover {
    background: ${({ color }) => {
      if (color === 'primary') return `${classic.token?.colorPrimaryBgHover}`;
      if (color === 'secondary')
        return `${classic.token?.colorBorderSecondary}`;

      return 'transparent';
    }};
  }
`;

//   border-radius: 0;
